<template>
  <div class="part table customers-selector">
    <a-table
      :columns="columns"
      :data-source="customers"
      :row-key="(customer, index) => customer._id"
      :scroll="{ x: 1200 }"
      :row-selection="{ selectedRowKeys: selectedKeys, onChange: onSelectChange }"
      :pagination="pagination"
      :loading="loading"
      @change="updateQuery"
      size="small"
      bordered
    >
      <template slot="title">
        All Customers List ({{ selectedKeys ? selectedKeys.length || 0 : 0 }}/{{ meta.total }})
      </template>
      <span slot="gender" slot-scope="gender">
        <div class="gender" :class="gender">
          <i v-if="gender === 'male'" class="icmn-man"></i>
          <i v-else-if="gender === 'female'" class="icmn-woman"></i>
          <i v-else class="icmn-man-woman"></i>
        </div>
      </span>

      <span slot="name" slot-scope="customer">
        <span class="name">
          <span class="first_name">
            {{ customer.name }}
          </span>
          <span class="last_name" v-if="customer.last_name">
            {{ customer.last_name }}
          </span>
        </span>
      </span>

      <span slot="status" slot-scope="status">
        <span class="font-size-12 badge" :class="statusClass(status)">{{ status }}</span>
      </span>

      <span slot="projects" slot-scope="projects">
        {{ projects.length }}
      </span>

      <span slot="locale" slot-scope="locale">
        {{ locale | LOCALE_ }}
      </span>

      <span slot="created_at" slot-scope="created_at">
        <span> {{ $moment(created_at).format('l') }} </span>
      </span>

      <span slot="updated_at" slot-scope="updated_at">
        <span> {{ updated_at ? $moment(updated_at).format('l') : '---' }} </span>
      </span>

      <span slot="created_by" slot-scope="created_by">
        <span> {{ created_by ? created_by.display_name : '---' }} </span>
      </span>
    </a-table>
  </div>
</template>

<script>
import options from '@/options'

export default {
  name: 'table-customers-selector',
  model: {
    prop: 'selectedKeys',
    event: 'change',
  },
  props: {
    selectedKeys: {
      type: Array,
      default: () => {
        return []
      },
    },
  },
  data() {
    return {
      selectedRowKeys: [],
      columns: [
        {
          title: 'ID',
          dataIndex: 'uid',
          key: 'uid',
          fixed: 'left',
          width: 70,
          align: 'center',
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.uid - b.uid,
        },
        {
          title: this.$t('crm.list.gender'),
          dataIndex: 'gender',
          key: 'gender',
          width: 70,
          align: 'center',
          scopedSlots: { customRender: 'gender' },
          filters: [
            { text: 'Male', value: 'male' },
            { text: 'Female', value: 'female' },
          ],
        },
        {
          title: this.$t('crm.list.fullname'),
          key: 'name',
          width: 160,
          scopedSlots: { customRender: 'name' },
        },
        {
          title: this.$t('crm.list.status'),
          dataIndex: 'status',
          key: 'status',
          align: 'center',
          width: 80,
          scopedSlots: { customRender: 'status' },
          filters: options.CUSTOMER_STATUS.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: false,
        },
        {
          title: this.$t('crm.list.projects'),
          dataIndex: 'projects',
          key: 'projects',
          align: 'center',
          width: 80,
          scopedSlots: { customRender: 'projects' },
        },
        {
          title: this.$t('crm.list.phone'),
          dataIndex: 'phone',
          key: 'phone',
          width: 100,
        },
        {
          title: this.$t('crm.list.email'),
          dataIndex: 'email',
          key: 'email',
          width: 180,
          ellipsis: true,
        },
        {
          title: this.$t('crm.list.location'),
          dataIndex: 'location',
          key: 'location',
          width: 150,
        },
        {
          title: this.$t('crm.list.locale'),
          dataIndex: 'locale',
          key: 'locale',
          width: 100,
          scopedSlots: { customRender: 'locale' },
          filters: options.LOCALES.map((s) => {
            return { text: s.value, value: s.key }
          }),
          filterMultiple: true,
        },

        {
          title: this.$t('crm.list.created'),
          dataIndex: 'created_at',
          key: 'created_at',
          width: 120,
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.created_at - b.created_at,
          scopedSlots: { customRender: 'created_at' },
        },
        {
          title: this.$t('crm.list.updated'),
          dataIndex: 'updated_at',
          key: 'updated_at',
          width: 120,
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.updated_at - b.updated_at,
          scopedSlots: { customRender: 'updated_at' },
        },
        {
          title: this.$t('crm.list.creator'),
          dataIndex: 'created_by',
          key: 'created_by',
          width: 120,
          scopedSlots: { customRender: 'created_by' },
        },
      ],
    }
  },
  computed: {
    loading() {
      return this.$store.getters['recipients/loading']('customers')
    },
    customers() {
      return this.$store.getters['recipients/data']('customers')
    },
    pagination() {
      return this.$store.getters['recipients/pagination']('customers')
    },
    meta() {
      return this.$store.getters['recipients/meta']('customers')
    },
  },
  methods: {
    async fetch() {
      await this.$store.dispatch('recipients/fetchAll', 'customers')
    },
    async updateQuery(pagination, filters, sorter) {
      this.$store.dispatch('recipients/updateQuery', { key: 'customers', query: { pagination, filters, sorter } })
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
      this.$emit('onSelectionChange', selectedRowKeys)
    },
    statusClass(status) {
      switch (status) {
        case 'active':
          return 'badge-primary'
        case 'inactive':
          return 'badge-danger'
        case 'pending':
          return 'badge-default'
        default:
          return 'badge-default'
      }
    },
  },
  async created() {
    await this.fetch()
    if (this.selectedKeys) {
      this.selectedRowKeys = this.selectedKeys
    }
  },
  async mounted() {
    // do nothing here
  },
}
</script>

<style lang="scss" scoped>
.table.customers-selector {
  .status {
    border: none;
    text-transform: capitalize;
    background: #666;
    color: #fff;
    &.active {
      background: #00b44b;
    }
  }

  .gender {
    color: #666;

    &.male {
      color: rgb(0, 81, 255);
    }
    &.female {
      color: rgb(255, 0, 0);
    }
  }
}
</style>
